import { useEffect, useState } from "react";
import { QueryClient, QueryClientProvider, useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  useRouterState,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";

import "../styles.css";
import oswald700 from "@fontsource/oswald/files/oswald-latin-700-normal.woff2?url";
import { getGaMeasurementId } from "@/lib/analytics.functions";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { runAfterLoad, runOnInteraction } from "@/lib/run-after-load";
import { SITE } from "@/lib/site";

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

const NOT_FOUND_LINKS = [
  { to: "/", label: "Home" },
  { to: "/refrigerated-freight", label: "Refrigerated Freight" },
  { to: "/dry-van-freight", label: "Dry Van Freight" },
  { to: "/coverage", label: "Coverage" },
  { to: "/drive-with-us", label: "Drive With Us" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Book a Load" },
] as const;

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen flex-col bg-background">
      <div className="diag-stripes h-2 w-full" />
      <div className="flex flex-1 flex-col items-center justify-center px-4 py-16">
        <div className="max-w-lg text-center">
          <p className="font-display text-xs tracking-[0.3em] text-primary">{SITE.name}</p>
          <h1 className="mt-4 font-display text-8xl text-primary">404</h1>
          <h2 className="mt-4 font-display text-2xl text-foreground">Off Route</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            This page took a wrong exit. Let&apos;s get you back on the highway.
          </p>
          <Link
            to="/"
            className="mt-8 inline-flex items-center justify-center rounded-md bg-primary px-5 py-3 font-display tracking-wider text-primary-foreground hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            Back home
          </Link>
          <nav aria-label="Popular pages" className="mt-10">
            <p className="font-display text-xs tracking-widest text-muted-foreground">Or jump to</p>
            <ul className="mt-4 flex flex-wrap justify-center gap-2">
              {NOT_FOUND_LINKS.map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    className="inline-flex rounded-md border border-border bg-card px-3 py-2 font-display text-xs tracking-wider text-card-foreground hover:border-primary hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
          <p className="mt-8 text-sm text-muted-foreground">
            Need dispatch?{" "}
            <a
              href={`tel:${SITE.phoneTel}`}
              className="font-display tracking-wider text-primary underline-offset-2 hover:underline"
            >
              {SITE.phoneDisplay}
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="font-display text-2xl text-foreground">This page didn't load</h1>
        <p className="mt-2 text-sm text-muted-foreground">Something went wrong on our end.</p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="rounded-md bg-primary px-4 py-2 font-display tracking-wider text-primary-foreground"
          >
            Try again
          </button>
          <a
            href="/"
            className="rounded-md border border-input bg-background px-4 py-2 font-display tracking-wider"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { name: "theme-color", content: "#292018" },
      { name: "color-scheme", content: "light" },
      { property: "og:site_name", content: SITE.name },
      { property: "og:type", content: "website" },
      { property: "og:locale", content: "en_US" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "google-site-verification", content: "sW6Z1mQs-BdE5EtKrrCkMWppkHi-k6WCfjd557re-7E" },
    ],
    links: [
      { rel: "icon", type: "image/png", href: "/favicon-32.png", sizes: "32x32" },
      { rel: "apple-touch-icon", href: "/apple-touch-icon.png" },
      // Only the hero headline face — fetchpriority=low so it does not beat the LCP image.
      {
        rel: "preload",
        href: oswald700,
        as: "font",
        type: "font/woff2",
        crossOrigin: "anonymous",
        fetchPriority: "low",
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en" suppressHydrationWarning>
      <head suppressHydrationWarning>
        <HeadContent />
      </head>
      <body suppressHydrationWarning>
        <a href="#main-content" className="skip-link">
          Skip to main content
        </a>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function GoogleAnalytics() {
  const loadMeasurementId = useServerFn(getGaMeasurementId);
  const [idle, setIdle] = useState(false);
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  useEffect(() => {
    return runOnInteraction(() => {
      if (typeof window.requestIdleCallback === "function") {
        window.requestIdleCallback(() => setIdle(true), { timeout: 2000 });
      } else {
        setIdle(true);
      }
    });
  }, []);

  const { data } = useQuery({
    queryKey: ["ga-measurement-id"],
    queryFn: loadMeasurementId,
    enabled: idle,
    staleTime: Infinity,
  });
  const measurementId = data?.measurementId ?? null;

  useEffect(() => {
    if (!measurementId) return;

    if (!window.gtag) {
      window.dataLayer = window.dataLayer || [];
      window.gtag = function gtag(...args: unknown[]) {
        window.dataLayer!.push(args);
      };
      window.gtag("js", new Date());

      const script = document.createElement("script");
      script.async = true;
      script.setAttribute("fetchpriority", "low");
      script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
      document.body.appendChild(script);
    }

    window.gtag!("config", measurementId);
    window.gtag!("event", "page_view", { page_path: pathname });
  }, [measurementId, pathname]);

  return null;
}

const LEADCONNECTOR_WIDGET_ID = "6a0e00bc85ae8362b2060635";

function LeadConnectorWidget() {
  useEffect(() => {
    return runAfterLoad(() => {
      if (document.querySelector(`script[data-widget-id="${LEADCONNECTOR_WIDGET_ID}"]`)) {
        return;
      }
      const script = document.createElement("script");
      script.src = "/leadconnector/loader.js";
      script.async = true;
      script.dataset.resourcesUrl = "/chat-widget/loader.js";
      script.dataset.widgetId = LEADCONNECTOR_WIDGET_ID;
      document.body.appendChild(script);
    });
  }, []);

  return null;
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  return (
    <QueryClientProvider client={queryClient}>
      <div className="flex min-h-screen flex-col">
        <SiteHeader />
        <main id="main-content" className="flex-1" tabIndex={-1}>
          <Outlet />
        </main>
        <SiteFooter />
      </div>
      <GoogleAnalytics />
      <LeadConnectorWidget />
    </QueryClientProvider>
  );
}
